
/* Shared foundation for the Alloy chart set (components/data-display/viz/*).
   Dependency-free SVG, measured in real pixels via ResizeObserver so text,
   strokes and markers keep their size at every width instead of scaling
   with a viewBox. Series colours come from the --ads-chart-* tokens
   (app/globals.css), referenced as CSS variables. */

import * as React from "react";
import { ArrowDownRight, ArrowUpRight, Info } from "lucide-react";
import { cn } from "@/lib/utils";

/* ── Palette ───────────────────────────────────────────────────────────── */

export type SeriesSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export const series = (slot: SeriesSlot) => `var(--ads-chart-${slot})`;
/** Slots whose fill is light enough that text on top must be ink, not white. */
export const LIGHT_SLOTS = new Set<SeriesSlot>([3, 4, 5]);
export const GRID = "var(--ads-chart-grid)";
export const HATCH = "var(--ads-chart-hatch)";

/* ── Sizing ────────────────────────────────────────────────────────────── */

/** Measures the container. `ready` is false on the server and first client
    render, so charts draw geometry only in the browser at the real width —
    no hydration mismatch from float formatting, no wrong-width flash. */
export function useChartWidth<T extends HTMLElement = HTMLDivElement>(fallback = 480) {
  // Callback ref: the observed node changes from the skeleton to the chart.
  const [el, setEl] = React.useState<T | null>(null);
  const [width, setWidth] = React.useState<number | null>(null);
  const [height, setHeight] = React.useState(0);
  const ref = React.useCallback(
    (node: T | null) => {
      setEl(node);
      // No ResizeObserver (older browsers, jsdom): take the first measurement straight from the node.
      if (node && typeof ResizeObserver === "undefined") {
        const r = node.getBoundingClientRect();
        setWidth(Math.max(0, Math.round(r.width)) || fallback);
        setHeight(Math.round(r.height));
      }
    },
    [fallback],
  );
  React.useLayoutEffect(() => {
    if (!el) return;
    if (typeof ResizeObserver === "undefined") {
      // Re-measure on window resize (the first measurement happens in the ref callback).
      const measure = () => {
        const r = el.getBoundingClientRect();
        setWidth(Math.max(0, Math.round(r.width)) || fallback);
        setHeight(Math.round(r.height));
      };
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    // ResizeObserver delivers an initial observation on observe(), which sets `ready`.
    const ro = new ResizeObserver(([entry]) => {
      setWidth(Math.max(0, Math.round(entry.contentRect.width)) || fallback);
      setHeight(Math.round(entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, fallback]);
  return { ref, width: width ?? fallback, height, ready: width !== null };
}

/** Same-size placeholder shown until the chart has measured itself. */
export function ChartSkeleton({ height, innerRef }: { height: number; innerRef: React.Ref<HTMLDivElement> }) {
  return <div ref={innerRef} aria-hidden className="w-full animate-pulse rounded-lg bg-bg" style={{ height }} />;
}

/* ── Geometry ──────────────────────────────────────────────────────────── */

export type Pt = [number, number];

/** Monotone cubic interpolation (Fritsch–Carlson) — smooth, never overshoots. */
export function monotonePath(pts: Pt[], moveTo = true): string {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `${moveTo ? "M" : "L"}${pts[0][0]},${pts[0][1]}`;
  const n = pts.length;
  const dx: number[] = [];
  const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0];
    m[i] = (pts[i + 1][1] - pts[i][1]) / (dx[i] || 1);
  }
  const t: number[] = [m[0]];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i], h = a * a + b * b;
    if (h > 9) { const s = 3 / Math.sqrt(h); t[i] = s * a * m[i]; t[i + 1] = s * b * m[i]; }
  }
  let d = `${moveTo ? "M" : "L"}${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += `C${pts[i][0] + h},${pts[i][1] + t[i] * h},${pts[i + 1][0] - h},${pts[i + 1][1] - t[i + 1] * h},${pts[i + 1][0]},${pts[i + 1][1]}`;
  }
  return d;
}

/** Round, human axis ticks between 0 (or min) and max. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * mag).find((s) => s >= raw) ?? 10 * mag;
  const start = Math.floor(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + step * 0.5; v += step) out.push(+v.toFixed(10));
  return out;
}

export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number) {
  const p0 = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
  const p1 = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
  const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
  return `M${p0[0]},${p0[1]}A${r},${r},0,${large},${a1 > a0 ? 1 : 0},${p1[0]},${p1[1]}`;
}

/* ── Hatch pattern (texture for "context" / "remaining" marks) ─────────── */

export function HatchDefs({ id, color = HATCH, stroke = "var(--ads-line)" }: { id: string; color?: string; stroke?: string }) {
  return (
    <pattern id={id} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width="7" height="7" style={{ fill: color }} />
      <line x1="0" y1="0" x2="0" y2="7" strokeWidth="2.2" style={{ stroke }} />
    </pattern>
  );
}

/* ── Tooltip (dark card, per the dotted-grid area reference) ───────────── */

export function ChartTooltip({
  x,
  y,
  containerWidth,
  children,
  placement = "above",
}: {
  x: number;
  y: number;
  containerWidth: number;
  children: React.ReactNode;
  placement?: "above" | "below";
}) {
  // Keep the card inside the chart: flip its anchor near the right edge.
  const alignRight = x > containerWidth - 150;
  const alignLeft = x < 90;
  return (
    <div
      role="status"
      className={cn(
        "pointer-events-none absolute z-20 whitespace-nowrap rounded-lg bg-ink px-3 py-2 text-xs text-white shadow-[0_12px_28px_color-mix(in_srgb,var(--ads-ink)_28%,transparent)]",
        placement === "above" ? "-translate-y-[calc(100%+12px)]" : "translate-y-3",
        alignRight ? "-translate-x-full" : alignLeft ? "" : "-translate-x-1/2"
      )}
      style={{ left: x, top: y }}
    >
      {children}
    </div>
  );
}

export function TooltipRow({ color, label, value }: { color?: string; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-5 leading-5">
      <span className="flex items-center gap-1.5 text-white/70">
        {color && <span aria-hidden className="size-2 rounded-[2px]" style={{ background: color }} />}
        {label}
      </span>
      <span className="font-semibold tabular-nums text-white">{value}</span>
    </div>
  );
}

/* ── Card chrome ───────────────────────────────────────────────────────── */

export function DeltaPill({
  direction,
  pct,
  good = direction === "up",
  suffix,
  className,
}: {
  direction: "up" | "down";
  pct: number;
  good?: boolean;
  suffix?: string;
  className?: string;
}) {
  const Icon = direction === "up" ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center gap-2 text-xs", className)}>
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold tabular-nums",
          good ? "border-success-border bg-success-soft text-success" : "border-danger-border bg-danger-soft text-danger"
        )}
      >
        <Icon aria-hidden className="size-3" />
        <span className="sr-only">{direction === "up" ? "Up" : "Down"}</span>
        {pct}%
      </span>
      {suffix && <span className="text-muted">{suffix}</span>}
    </span>
  );
}

/** Info icon whose explanation renders only while open, anchored to the card's
    inner width — so a hidden hint can never widen the page. */
function InfoHint({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  return (
    <>
      <button
        type="button"
        aria-label="About this chart"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onPointerEnter={() => setOpen(true)}
        onPointerLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        className="grid size-5 shrink-0 place-items-center rounded-full text-subtle outline-none hover:text-muted focus-visible:ring-2 focus-visible:ring-primary-soft"
      >
        <Info aria-hidden className="size-3.5" />
      </button>
      {open && (
        <span id={id} role="tooltip" className="absolute inset-x-5 top-[52px] z-30 rounded-lg bg-ink px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-[0_12px_28px_color-mix(in_srgb,var(--ads-ink)_28%,transparent)]">
          {text}
        </span>
      )}
    </>
  );
}

export function ChartCard({
  title,
  icon,
  info,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  info?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("relative flex min-w-0 flex-col rounded-[16px] border border-line bg-surface p-5", className)}>
      <header className="mb-4 flex min-h-8 items-center justify-between gap-3">
        <h3 className="flex min-w-0 items-center gap-2 text-[15px] font-semibold text-ink">
          {icon && <span aria-hidden className="shrink-0 text-primary [&>svg]:size-[18px]">{icon}</span>}
          <span className="truncate">{title}</span>
          {info && <InfoHint text={info} />}
        </h3>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      <div className={cn("min-w-0 flex-1", bodyClassName)}>{children}</div>
    </section>
  );
}

/** Round ghost "↗" button used in card headers (per the Dashboard UI Charts reference). */
export function CardLinkButton({ href, label, onClick }: { href?: string; label: string; onClick?: () => void }) {
  const cls =
    "grid size-8 place-items-center rounded-full bg-bg text-ink transition-colors hover:bg-primary-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft";
  const icon = <ArrowUpRight aria-hidden className="size-4" />;
  if (href) {
    return (
      <a href={href} aria-label={label} className={cls}>
        {icon}
      </a>
    );
  }
  return (
    <button type="button" aria-label={label} onClick={onClick} className={cls}>
      {icon}
    </button>
  );
}

/** Pill segmented control (Week / Month / Year reference). */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: T; label: string; disabled?: boolean; title?: string }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex rounded-full bg-bg p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={o.disabled}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={cn(
            "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:px-3",
            value === o.value ? "bg-surface text-ink shadow-[0_1px_3px_color-mix(in_srgb,var(--ads-ink)_12%,transparent)]" : "text-muted hover:text-ink"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Legend row: swatch + label (+ optional value). Text stays in ink/muted. */
export function LegendItem({ color, label, value, hatch }: { color: string; label: string; value?: string; hatch?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-text">
      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-[3px]"
        style={
          hatch
            ? { backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 5px)`, boxShadow: `inset 0 0 0 1px ${color}` }
            : { background: color }
        }
      />
      {label}
      {value && <span className="font-semibold tabular-nums text-ink">{value}</span>}
    </span>
  );
}
